import { useCallback } from "react";
import { Position } from "reactflow";
import { snapPositionToGrid } from "../../utils/grid";
import useStore from "../../store/useStore";
import { CustomNode, Integration, NodeType, SourceType, SourceNodeData, PipelineNodeData, SyncInjectorNodeData, LoadBalancerNodeData, EventNodeData, FilterNodeData, ModuleConfig, MODULES, ENVIRONMENTS, AnyNodeData, HandleConfig } from "../../types";
import { VisibilityOption } from "../../components/shared/LabelWithVisibilityToggle";
import { hasOverlap, findNonOverlappingPosition, calculateClusterNodePosition, calculateSyncInjectorPosition } from "./utils";

interface NodeCreatorProps {
  currentProject: string;
  onAdd: (node: CustomNode) => void;
  reactFlowWrapper: React.RefObject<HTMLDivElement>;
  fitView?: (options?: any) => void;
}

// New function to define default handles based on node type
export const getDefaultHandles = (nodeType: NodeType): HandleConfig[] => {
  const handles: HandleConfig[] = [];

  if (nodeType === 'source' || nodeType === 'javascript-source' || nodeType === 'mobile-sdk' || nodeType === 'server-source') {
    handles.push({
      id: 'input-default',
      type: 'target',
      position: Position.Left,
      color: '#615fff', // Indigo-500
    });
    handles.push({
      id: 'output-default',
      type: 'source',
      position: Position.Right,
      color: '#3b82f6', // Blue-500
    });
  } else if (nodeType === 'destination') {
    handles.push({
      id: 'input-default',
      type: 'target',
      position: Position.Left,
      color: '#22c55e', // Green-500
    });
  } else if (nodeType === 'sync-injector') {
    handles.push({
      id: 'output-default',
      type: 'source',
      position: Position.Right,
      color: '#615fff', // Indigo-500
    });
  } else if (nodeType === 'load-balancer') {
    handles.push({
      id: 'input-default',
      type: 'target',
      position: Position.Left,
      color: '#3b82f6', // Blue-500
    });
    handles.push({
      id: 'output-default',
      type: 'source',
      position: Position.Right,
      color: '#3b82f6', // Blue-500
    });
  } else if (nodeType === 'cluster') {
    handles.push({
      id: 'input-default',
      type: 'target',
      position: Position.Left,
      color: '#3b82f6', // Blue-500
    });
    handles.push({
      id: 'output-default',
      type: 'source',
      position: Position.Right,
      color: '#22c55e', // Green-500
    });
  } else if (
    nodeType === 'pipeline' ||
    nodeType === 'filter' ||
    nodeType === 'event' ||
    nodeType === 'transformations' ||
    nodeType === 'schema-enforcement' ||
    nodeType === 'bot-filtering' ||
    nodeType === 'data-replay'
  ) {
    handles.push({
      id: 'input-default',
      type: 'target',
      position: Position.Left,
      color: '#6b7280', // Gray-500
    });
    handles.push({
      id: 'output-default',
      type: 'source',
      position: Position.Right,
      color: '#6b7280', // Gray-500
    });
  }
  return handles;
};

export const useNodeCreator = ({
  currentProject,
  onAdd,
  reactFlowWrapper,
  fitView
}: NodeCreatorProps) => {
  const nodes = useStore((state) => state.nodes);
  const setNodes = useStore((state) => state.setNodes); // Get setNodes from Zustand

  const createNodeData = useCallback((
    nodeType: NodeType, 
    itemName: string, 
    itemType: string, 
    integration?: Integration,
    addHandles: boolean = true // New parameter
  ) => {
    // Common base data for all node types
    const baseData = {
      label: itemName,
      type: nodeType === "destination" ? "destination" : itemType,
      project: currentProject,
    };
    
    // Add integration-specific data if available
    if (integration) {
      return {
        ...baseData,
        name: itemName, // Add name field for destination nodes
        icon: integration.logo_url,
        category: integration.category,
        description: integration.description,
        docs: integration.documentation || undefined,
        latencyImpact: typeof integration.latency_impact === 'string' 
          ? parseInt(integration.latency_impact, 10) 
          : integration.latency_impact || 0, // Corrected typo here
        // --- Add default handles FOR DRAG DATA ---
        handles: addHandles ? getDefaultHandles(nodeType) : [], // Use addHandles
        // --- End default handles ---
      };
    }
    
    // Add source-specific data
    if (nodeType === "source") {
      let subType: SourceType = "javascript";
      
      if (itemName === "Website") {
        subType = "javascript";
      } else if (itemName === "Mobile SDK") {
        subType = "mobile-sdk";
      } else if (itemName === "Server/other") {
        subType = "server";
      }
      
      return {
        ...baseData,
        subType,
        handles: addHandles ? getDefaultHandles(nodeType) : [], // Use addHandles for source
      };
    }
    
    // Add cluster-specific data
    if (nodeType === "cluster") {
      // Get default environment
      const defaultEnv = ENVIRONMENTS.find(env => env.id === 'mr-managed');
      
      return {
        ...baseData,
        clusterEnvironment: {
          environmentId: 'mr-managed',
          name: 'MetaRouter Managed',
          teamAccessVisibility: "hidden" as VisibilityOption,
          hintText: defaultEnv?.hintText
        },
        // Explicitly set the visibility at the node level as well
        teamAccessVisibility: "hidden" as VisibilityOption,
        handles: addHandles ? getDefaultHandles(nodeType) : [],
      };
    }

    // Add pipeline-specific data
    if (nodeType === "pipeline") {
      // Create default enabled modules for pipeline
      const defaultModules: ModuleConfig[] = MODULES
        .filter(m => m.isEnabled)
        .map(m => ({
          moduleId: m.id,
          enabled: false
        }));

      return {
        ...baseData,
        name: itemName, // Add name property
        modules: defaultModules,
        handles: addHandles ? getDefaultHandles(nodeType) : [],
      };
    }

    // Add sync-injector-specific data
    if (nodeType === "sync-injector") {
      return {
        ...baseData,
        status: "active",
        domains: [],
        handles: addHandles ? getDefaultHandles(nodeType) : [],
      };
    }

    // Add load-balancer-specific data
    if (nodeType === "load-balancer") {
      return {
        ...baseData,
        handles: addHandles ? getDefaultHandles(nodeType) : [],
      };
    }

    // Add filter-specific data
    if (nodeType === "filter") {
      return {
        ...baseData,
        filterType: "",
        handles: addHandles ? getDefaultHandles(nodeType) : [],
      };
    }

    // Add event-specific data
    if (nodeType === "event") {
      return {
        ...baseData,
        name: "Event", // Ensure name is included for drag data
        handles: addHandles ? getDefaultHandles(nodeType) : [],
      };
    }

    // Add transformations-specific data
    if (nodeType === "transformations") {
      return {
        ...baseData,
        nodeType: 'transformations',
        handles: addHandles ? getDefaultHandles(nodeType) : [],
      };
    }

    // Add schema-enforcement-specific data
    if (nodeType === "schema-enforcement") {
      return {
        ...baseData,
        nodeType: 'schema-enforcement',
        handles: addHandles ? getDefaultHandles(nodeType) : [],
      };
    }

    // Add bot-filtering-specific data
    if (nodeType === "bot-filtering") {
      return {
        ...baseData,
        nodeType: 'bot-filtering',
        handles: addHandles ? getDefaultHandles(nodeType) : [],
      };
    }

    // Add data-replay-specific data
    if (nodeType === "data-replay") {
      return {
        ...baseData,
        nodeType: 'data-replay',
        handles: addHandles ? getDefaultHandles(nodeType) : [],
      };
    }

    // Add group-specific data
    if (nodeType === "group") {
      return {
        name: itemName, // Specific to GroupNodeData
        label: itemName, // Common property
        type: itemType,  // Internal category, e.g., "group". itemType is passed from createNodeData's 3rd arg.
        project: currentProject, // Common property

        // Obsolete fields apparently still expected by the GroupNodeData type in ../../types
        hasIncomingHandle: false, // Default to false
        hasOutgoingHandle: false, // Default to false

        // Core GroupNodeData fields (matching src/node-types/group-node/types.ts)
        color: undefined, // Ensures no color is initially set for default styling
        colorOpacity: undefined,
        allowedNodeTypes: [],
        customLabel: "",
        groupNameVisibility: "visible" as VisibilityOption,
        iconVisibility: "visible" as VisibilityOption,
        customLabelVisibility: "visible" as VisibilityOption,
        handles: addHandles ? [] : [], // Group nodes typically don't have default handles, but respect addHandles
        icon: "layers", // Default icon string for group node data
      };
    }
    
    return {
      ...baseData,
      handles: addHandles ? getDefaultHandles(nodeType) : [], // Fallback for other types
    };
  }, [currentProject]);

  const handleAdd = useCallback(
    (nodeType: NodeType, itemType: string, integration?: Integration, customPosition?: { x: number, y: number }, addHandles: boolean = true): CustomNode | null => { // New parameter
      console.log("[NodeCreator] handleAdd called. addHandles:", addHandles);
      if (!reactFlowWrapper?.current) {
        console.warn("Canvas ref not available");
        return null;
      }

      const canvasRect = reactFlowWrapper.current.getBoundingClientRect();
      
      // Get current viewport for positioning
      const viewport = useStore.getState().viewport;
      
      // Get initial position - either use provided position or calculate one
      let position;
      
      if (customPosition) {
        // If a custom position is provided (e.g. from keyboard navigation)
        // Make sure to apply viewport transform
        if (viewport) {
          // Apply the same transformation as in onDrop
          const relativePosition = {
            x: customPosition.x - canvasRect.left,
            y: customPosition.y - canvasRect.top
          };
          
          // Apply viewport transform
          position = {
            x: (relativePosition.x - viewport.x) / viewport.zoom,
            y: (relativePosition.y - viewport.y) / viewport.zoom
          };
          
          // Snap to grid
          position = snapPositionToGrid(position);
        } else {
          position = snapPositionToGrid(customPosition);
        }
      } else {
        // Calculate a position that's in the viewport
        position = findNonOverlappingPosition(canvasRect, viewport, nodes);
        // Snap to grid
        position = snapPositionToGrid(position);
      }

      // Find the most recent web source node (used for sync-injector)
      const webSources = nodes.filter(
        (node) =>
          node.type === "source" &&
          "subType" in node.data &&
          (node.data as SourceNodeData).subType === "javascript",
      );
      const mostRecentWebSource =
        webSources.length > 0 ? webSources[webSources.length - 1] : null;

      // Find the newest cluster node (used for multiple node types)
      const clusterNodes = nodes.filter((node) => node.type === "cluster");
      const newestClusterNode = clusterNodes.reduce((prev, current) => {
        return prev && current.id > prev.id ? current : prev;
      }, clusterNodes[0]);

      // Position nodes based on their type
      if (newestClusterNode) {
        if (
          nodeType === "source" ||
          nodeType === "load-balancer" ||
          nodeType === "destination"
        ) {
          const relativePos = calculateClusterNodePosition(nodeType, newestClusterNode, nodes);
          // Only use relative positioning if it doesn't cause overlap
          if (!hasOverlap(relativePos.x, relativePos.y, nodes)) {
            position = relativePos;
          } else {
            // If the relative position causes overlap, try to find a nearby non-overlapping position
            // Start with the relative position and move outward
            const nodeWidth = 200;
            const minDistance = 80;
            
            // Try positions in a spiral pattern around the relative position
            const spiralSize = 3; // How many "rings" to try
            let foundPosition = false;
            
            outerLoop: for (let ring = 1; ring <= spiralSize; ring++) {
              const step = (nodeWidth + minDistance) * ring;
              
              // Try positions in a grid around the relative position
              for (let i = -1; i <= 1; i++) {
                for (let j = -1; j <= 1; j++) {
                  // Skip the center position (already checked)
                  if (i === 0 && j === 0) continue;
                  
                  const testPos = {
                    x: relativePos.x + i * step,
                    y: relativePos.y + j * step
                  };
                  
                  if (!hasOverlap(testPos.x, testPos.y, nodes)) {
                    position = testPos;
                    foundPosition = true;
                    break outerLoop;
                  }
                }
              }
            }
            
            // If still no position found, fall back to the general algorithm
            if (!foundPosition) {
            }
          }
        }
      } else if (nodeType === "sync-injector" && mostRecentWebSource) {
        const relativePos = calculateSyncInjectorPosition(mostRecentWebSource);
        // Only use relative positioning if it doesn't cause overlap
        if (!hasOverlap(relativePos.x, relativePos.y, nodes)) {
          position = relativePos;
        } else {
          // Try positions with increasing vertical offset
          for (let offset = 80; offset <= 320; offset += 80) {
            const testPos = {
              x: relativePos.x,
              y: relativePos.y + offset
            };
            
            if (!hasOverlap(testPos.x, testPos.y, nodes)) {
              position = testPos;
              break;
            }
          }
        }
      }
      
      // Final check to ensure we don't have overlap
      if (hasOverlap(position.x, position.y, nodes)) {
        position = findNonOverlappingPosition(canvasRect, viewport, nodes);
      }

      let newNode: CustomNode | null = null;

      // Create the node based on type
      if (nodeType === "source") {
        let subType: SourceType = "javascript";
        let label = itemType;
        let specificNodeType: NodeType = "source";

        if(itemType === "Website") {
          subType = "javascript";
          specificNodeType = "javascript-source";
        } else if(itemType === "Mobile SDK") {
          subType = "mobile-sdk";
          specificNodeType = "mobile-sdk";
        } else if (itemType === "Server/other") {
          subType = "server";
          specificNodeType = "server-source";
        }

        newNode = {
          id: `${specificNodeType}-${Date.now()}`,
          type: specificNodeType,
          data: {
            label: label,
            type: "source",
            project: currentProject,
            subType: subType,
            targets: newestClusterNode ? [newestClusterNode.id] : [],
            handles: addHandles ? getDefaultHandles(specificNodeType) : [], // Use addHandles
          },
          position,
        };
      } else if (nodeType === "destination" && integration) {
        newNode = {
          id: `${nodeType}-${integration.id}-${Date.now()}`,
          type: nodeType,
          data: {
            label: integration.name,
            name: integration.name,
            type: "destination",
            icon: integration.logo_url,
            project: currentProject,
            docs: integration.documentation,
            category: integration.category,
            description: integration.description,
            latencyImpact: (() => { // Calculate latencyImpact first
              const impact = typeof integration.latency_impact === 'string'
                ? parseInt(integration.latency_impact, 10)
                : integration.latency_impact;
              // Return the parsed number or null if invalid/missing
              return (typeof impact === 'number' && !isNaN(impact)) ? impact : null;
            })(),
            targets: newestClusterNode ? [newestClusterNode.id] : [],
            // --- Force initial visibility to hidden for ConfigBox ---
            latencyImpactVisibility: 'hidden', // Always start hidden in ConfigBox
            handles: addHandles ? getDefaultHandles(nodeType) : [], // Use addHandles
          },
          position,
        };
      } else if (nodeType === "cluster") {
        // Get default environment
        const defaultEnv = ENVIRONMENTS.find(env => env.id === 'mr-managed');
        
        // Create default enabled modules
        const defaultModules: ModuleConfig[] = MODULES
          .filter(m => m.isEnabled)
          .map(m => ({
            moduleId: m.id,
            enabled: false
          }));

        newNode = {
          id: `${nodeType}-${itemType}-${Date.now()}`,
          type: nodeType,
          data: {
            label: `${itemType}`,
            type: "cluster",
            project: currentProject,
            clusterEnvironment: {
              environmentId: defaultEnv?.id || 'mr-managed',
              name: defaultEnv?.name || 'MetaRouter Managed',
              teamAccessVisibility: "hidden" as VisibilityOption, // Explicitly cast to VisibilityOption
              hintText: defaultEnv?.hintText
            },
            modules: defaultModules,
            handles: addHandles ? getDefaultHandles(nodeType) : [],
          },
          position,
        };
      } else if (nodeType === "pipeline") {
        // Create default enabled modules for pipeline
        const defaultModules: ModuleConfig[] = MODULES
          .filter(m => m.isEnabled)
          .map(m => ({
            moduleId: m.id,
            enabled: false
          }));

        newNode = {
          id: `${nodeType}-${itemType}-${Date.now()}`,
          type: nodeType,
          data: {
            label: itemType,
            type: "pipeline",
            name: itemType,
            project: currentProject,
            modules: defaultModules,
            handles: addHandles ? getDefaultHandles(nodeType) : [],
          } as PipelineNodeData,
          position,
        };
      } else if (nodeType === "sync-injector") {
        newNode = {
          id: `${nodeType}-${itemType}-${Date.now()}`,
          type: nodeType,
          data: {
            label: `${itemType}`,
            type: "sync-injector",
            project: currentProject,
            status: "active",
            domains: [],
            targets: mostRecentWebSource ? [mostRecentWebSource.id] : [],
            handles: addHandles ? getDefaultHandles(nodeType) : [],
          } as SyncInjectorNodeData,
          position,
        };
      } else if (nodeType === "load-balancer") {
        newNode = {
          id: `${nodeType}-${itemType}-${Date.now()}`,
          type: nodeType,
          data: {
            label: itemType, // Add label for load balancer
            project: currentProject,
            targets: newestClusterNode ? [newestClusterNode.id] : [],
            handles: addHandles ? getDefaultHandles(nodeType) : [],
          } as LoadBalancerNodeData,
          position,
        };
      } else if (nodeType === "filter") {
        newNode = {
          id: `filter-${Date.now()}`,
          type: 'filter', 
          data: {
            label: "Filter",
            type: "filter",
            project: currentProject,
            filterType: "",
            handles: addHandles ? getDefaultHandles(nodeType) : [],
          } as FilterNodeData,
          position,
        };
      } else if (nodeType === "event") {
        newNode = {
          id: `event-${itemType}-${Date.now()}`,
          type: 'event', 
          data: {
            label: "",
            name: "Event",
            type: "event",
            project: currentProject,
            handles: addHandles ? getDefaultHandles(nodeType) : [],
          } as EventNodeData,
          position,
        };
      } else if (nodeType === "transformations") {
        newNode = {
          id: `transformations-${Date.now()}`,
          type: 'transformations',
          data: {
            label: "Transformations",
            type: "processor",
            nodeType: 'transformations',
            project: currentProject,
            handles: addHandles ? getDefaultHandles(nodeType) : [],
          },
          position,
        };
      } else if (nodeType === "schema-enforcement") {
        newNode = {
          id: `schema-enforcement-${Date.now()}`,
          type: 'schema-enforcement',
          data: {
            label: "Schema Enforcement",
            type: "processor",
            nodeType: 'schema-enforcement',
            project: currentProject,
            handles: addHandles ? getDefaultHandles(nodeType) : [],
          },
          position,
        };
      } else if (nodeType === "bot-filtering") {
        newNode = {
          id: `bot-filtering-${Date.now()}`,
          type: 'bot-filtering',
          data: {
            label: "Bot Filtering",
            type: "processor",
            nodeType: 'bot-filtering',
            project: currentProject,
            handles: addHandles ? getDefaultHandles(nodeType) : [],
          },
          position,
        };
      } else if (nodeType === "data-replay") {
        newNode = {
          id: `data-replay-${Date.now()}`,
          type: 'data-replay',
          data: {
            label: "Data Replay",
            type: "processor",
            nodeType: 'data-replay',
            project: currentProject,
            handles: addHandles ? getDefaultHandles(nodeType) : [],
          },
          position,
        };
      } else if (nodeType === "group") {
        const completeNodeData = createNodeData(nodeType, itemType, nodeType, undefined, addHandles);

        newNode = {
          id: `group-${Date.now()}`,
          type: "group",
          data: {
            ...completeNodeData as AnyNodeData,
          },
          position,
        };
      }

      if (newNode) {
        onAdd(newNode);
        setNodes((nds) =>
          nds.map((node) => {
            if (node.id === newNode.id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  // Add a dummy property that changes to trigger useEffect
                  _dummyUpdate: Date.now(), 
                },
              };
            }
            return node;
          })
        );
        

        
        // Animate fitView to show all nodes with a gentle animation
        setTimeout(() => {
          if (fitView) {
            // Call fitView with animation parameters but without specifying nodes
            // This will fit the view to show all nodes
            fitView({
              duration: 800, // Animation duration in ms
              padding: 0.2,  // Add some padding around the nodes
            });
          }
        }, 50); // Reduced timeout for more immediate feedback
        
        return newNode;
      }
      
      return null;
    },
    [onAdd, currentProject, nodes, reactFlowWrapper, createNodeData, fitView, setNodes], // Added setNodes to dependencies
  );

  return {
    handleAdd,
    createNodeData
  };
};
